// COMP-PATIENT-MESSAGES — تاریخچه و ارسال پیام در پرونده مراجعه‌کننده (US-MSG-071، 080؛ AP-MSG-01): هر پیام با زمان، کانال، قالب، وضعیت و
// علت عدم ارسال؛ متن کامل فقط با msg.log.view_content (و فقط ۹۰ روز)؛ پیام دستی با قالب «پیام آزاد» (بررسی واژه ممنوع و طول — BR-MSG-080)
// و ثبت فرستنده.
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type MessageLogItem } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { COMP_PATIENT_MESSAGES, msgKeys } from './common';
import { MessageDetailDialog, useMessageColumns } from './MessageViews';

const MAX_TEXT = 330;

export function PatientMessagesPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const send = useWriteAccess('msg.send.manual');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<MessageLogItem | null>(null);
  const [composing, setComposing] = useState(false);
  const [text, setText] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const history = useQuery({
    queryKey: msgKeys.patient(session?.tenantId, patientId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/messaging/patients/{patientId}/messages', {
          params: { path: { patientId }, query: { limit: 100 } },
        }),
      ),
    enabled: access.can('msg.log.view'),
  });
  const submit = useMutation({
    mutationFn: (value: string) =>
      unwrap(
        tenantApi.POST('/api/v1/messaging/patients/{patientId}/messages', {
          params: { path: { patientId } },
          body: { text: value },
        }),
      ),
    onSuccess: (result) => {
      setComposing(false);
      setText('');
      setSent(result.status);
      void queryClient.invalidateQueries({ queryKey: msgKeys.patient(session?.tenantId, patientId) });
    },
  });
  const columns = useMessageColumns(setOpen, false);

  return (
    <Stack spacing={2} data-screen-id={COMP_PATIENT_MESSAGES}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h3" sx={{ flex: 1 }}>
          {t('msg:patient.title')}
        </Typography>
        {send.permitted ? (
          <Button
            variant="contained"
            startIcon={<SendOutlinedIcon />}
            onClick={() => setComposing(true)}
            disabled={!send.canWrite}
            data-testid="patient-message-compose"
          >
            {t('msg:patient.compose')}
          </Button>
        ) : null}
      </Stack>
      {sent ? (
        <Alert
          severity={sent === 'Suppressed' || sent === 'Failed' ? 'warning' : 'success'}
          onClose={() => setSent(null)}
          data-testid="patient-message-sent"
          data-status={sent}
        >
          {t('msg:patient.sent', { status: t(`msg:status.${sent}`) })}
        </Alert>
      ) : null}
      {access.can('msg.log.view') ? (
        <DataTable
          captionKey="msg:patient.caption"
          columns={columns}
          rows={history.data?.items}
          getRowId={(row) => row.id}
          isLoading={history.isPending}
          error={history.error}
          onRetry={() => void history.refetch()}
          emptyTitleKey="msg:patient.empty"
        />
      ) : null}
      {open ? <MessageDetailDialog messageId={open.id} onClose={() => setOpen(null)} /> : null}
      <FormDialog
        open={composing}
        titleKey="msg:patient.compose_title"
        screenId={COMP_PATIENT_MESSAGES}
        onSubmit={() => submit.mutate(text)}
        onClose={() => {
          setComposing(false);
          setText('');
          submit.reset();
        }}
        isDirty={text.length > 0}
        isSubmitting={submit.isPending}
        submitError={submit.error}
        submitKey="msg:patient.send"
      >
        <Alert severity="info">{t('msg:patient.free_text_hint')}</Alert>
        <TextField
          label={t('msg:patient.text')}
          value={text}
          onChange={(event) => setText(event.target.value)}
          multiline
          minRows={3}
          required
          helperText={t('msg:manual.length', { count: faDigits(text.length) })}
          slotProps={{ htmlInput: { maxLength: MAX_TEXT, 'data-testid': 'patient-message-text' } }}
        />
      </FormDialog>
    </Stack>
  );
}
